import { modules } from "byzantine";
import App from "byzantine/src/App";
import { useLocalization } from "@fluent/react";
import { InstitutionFeatures } from "byzantine/src/types";
import { translateOAuthApplication } from "byzantine/src/l10n/SettingLocalizers";
import { useNotificationContext } from "cerulean";
import { useUserFeatures } from "../contexts/UserFeaturesContext";
import SectionCard from "../SectionCard";
import AppAuthorize from "../AppAuthorize";
import useIsServicesEnabled from "./ServicesCard.hooks";

const AoiobItem = () => {
  const { l10n } = useLocalization();
  const { sendNegativeNotification } = useNotificationContext();
  const { fetchAoiobLink } = modules.aoiob.useFetchAoiobLink();

  const handleClick = () => {
    fetchAoiobLink({
      onSuccess: ({ link }: { link: string }) => {
        const success = window.open(link, "_blank");
        // important not to check falsey
        // null is returned if the new browsing context fails
        if (success === null) {
          sendNegativeNotification(
            l10n.getString(
              "services-item-open-an-account-popup-failed-error",
              null,
              "We cannot open the application. Please adjust your browser's pop-up settings."
            )
          );
        }
      },
    });
  };

  return (
    <div className="dashboard-item-border-box">
      <div className="brand-hover">
        <button
          type="button"
          data-classname="AppAuthorize services-button"
          className="button--reset alignChild--left--top"
          onClick={handleClick}
        >
          <div className="dashboard-item" data-testid="open-an-account-item">
            <div className="dashboard-item-subsection dashboard-item-icon-section">
              <span className="narmi-icon-plus-square"></span>
            </div>
            <div className="dashboard-item-subsection dashboard-item-text-section">
              <div className="dashboard-item-name">
                {l10n.getString(
                  "services-item-open-an-account",
                  {},
                  "Open an account"
                )}
              </div>
              <div className="dashboard-item-description">
                {l10n.getString(
                  "services-item-open-an-account-description",
                  {},
                  "Open an account with us!"
                )}
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

interface ServiceItemProps {
  service: App;
}
const ServiceItem = ({ service }: ServiceItemProps) => {
  const { l10n } = useLocalization();

  const translatedApp = translateOAuthApplication(
    {
      uuid: service.id,
      name: service.name,
      description: service.description,
    },
    l10n
  );

  return (
    <div className="dashboard-item-border-box">
      <div className="brand-hover">
        <AppAuthorize app={service} dataClassnames="services-button">
          <div className="dashboard-item">
            <div className="dashboard-item-subsection dashboard-item-icon-section">
              <span className={service.getNdsIconClassname()}></span>
            </div>
            <div className="dashboard-item-subsection dashboard-item-text-section">
              <div className="dashboard-item-name">{translatedApp.name}</div>
              <div className="dashboard-item-description">
                {translatedApp.description}
              </div>
            </div>
          </div>
        </AppAuthorize>
      </div>
    </div>
  );
};

interface ServicesCardProps {
  services: App[];
}

const ServicesCard = ({ services }: ServicesCardProps) => {
  const { l10n } = useLocalization();
  const { aoiob } = useUserFeatures() as InstitutionFeatures;
  const isVisible = useIsServicesEnabled(services);
  if (!isVisible) return null;

  return (
    <SectionCard isLoading={false} classNames="dashboard-section-card">
      <SectionCard.Title
        text={l10n.getString("heading-services")}
        className="services-title"
      />
      <div className="dashboard-section-container">
        {services.map((service) => (
          <ServiceItem key={service.id} service={service} />
        ))}
        {aoiob && <AoiobItem />}
      </div>
    </SectionCard>
  );
};

export default ServicesCard;
